import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { FormField } from "../components/shared/FormField";
import { Sparkles, Search } from "lucide-react";
import { useForm } from "react-hook-form";
import * as Icons from "lucide-react";
import clsx from "clsx";
import { useAdminT } from "../../../hooks/useAdminT";
import { useFormValidation } from "../../../hooks/useFormValidation";
import toast from "react-hot-toast";

const ALL_ICONS = Object.keys(Icons)
    .filter(key => key !== 'createLucideIcon' && key !== 'default' && /^[A-Z]/.test(key))
    .sort();

const getTrans = (translations: any[], locale: string, field: string) =>
    translations?.find((t: any) => t.locale === locale)?.[field] || "";

export default function AmenitiesPage() {
    const queryClient = useQueryClient();
    const { t } = useAdminT();
    const { handleServerErrors } = useFormValidation();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);
    const [iconSearch, setIconSearch] = useState("");

    const { register, handleSubmit, reset, setValue, watch, setError, formState: { errors } } = useForm<any>();
    const currentIcon = watch("icon");

    const filteredIcons = useMemo(() => {
        if (!iconSearch) return ALL_ICONS;
        return ALL_ICONS.filter(icon => icon.toLowerCase().includes(iconSearch.toLowerCase()));
    }, [iconSearch]);

    const { data: amenities, isLoading } = useQuery({
        queryKey: ["amenities"],
        queryFn: () => api.get("/api/admin/amenities").then(res => res.data.data)
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/amenities", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["amenities"] });
            toast.success("Amenity saved successfully");
            handleClose();
        },
        onError: (err: any) => handleServerErrors(err, setError, "Failed to save amenity"),
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/amenities/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["amenities"] });
            toast.success("Amenity updated");
            handleClose();
        },
        onError: (err: any) => handleServerErrors(err, setError, "Failed to update amenity"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/amenities/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["amenities"] });
            toast.success("Amenity deleted");
            setDeleteTarget(null);
        },
        onError: () => {
            toast.error("Failed to delete amenity");
            setDeleteTarget(null);
        }
    });

    const handleClose = () => { setIsDrawOpen(false); setEditingId(null); reset(); };

    const handleEdit = useCallback((item: any) => {
        setEditingId(item.id);
        reset({
            nameEn: getTrans(item.translations, "en", "name"),
            nameAr: getTrans(item.translations, "ar", "name"),
            icon: item.icon || "",
        });
        setIsDrawOpen(true);
    }, [reset]);

    const onSubmit = (data: any) => {
        const payload = {
            icon: data.icon || null,
            translations: [
                { locale: "en", name: data.nameEn },
                { locale: "ar", name: data.nameAr },
            ],
        };
        if (editingId) { updateMutation.mutate(payload); } else { createMutation.mutate(payload); }
    };

    return (
        <div className="p-8">
            <PageHeader
                title={t("amenities.title")}
                description={t("amenities.subtitle")}
                actions={[{ label: t("amenities.add"), icon: Sparkles, onClick: () => setIsDrawOpen(true) }]}
            />

            <AdminTable
                data={amenities}
                isLoading={isLoading}
                columns={[
                    {
                        header: t("amenities.icon"),
                        cell: (a) => {
                            const Icon = a.icon && (Icons as any)[a.icon] ? (Icons as any)[a.icon] : Sparkles;
                            return <Icon className="text-slate-500" size={20} />;
                        },
                        enableSorting: false
                    },
                    { header: t("common.nameEn"), cell: (a) => getTrans(a.translations, "en", "name") || "-" },
                    { header: t("common.nameAr"), cell: (a) => getTrans(a.translations, "ar", "name") || "-" },
                    { header: t("nav.projects"), cell: (a) => a._count?.projects ?? 0 },
                ]}
                onEdit={handleEdit}
                onDelete={(a) => setDeleteTarget(a)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("amenities.delete")}
                message={`${t("common.confirmProceed")} "${getTrans(deleteTarget?.translations, "en", "name") || t("amenities.title")}"?`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("amenities.edit") : t("amenities.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button onClick={handleSubmit(onSubmit)} className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark">
                            {editingId ? t("common.save") : t("common.create")} {t("amenities.title")}
                        </button>
                    </>
                }
            >
                <form className="space-y-6">
                    <div className="space-y-3">
                        <label className="block text-sm font-medium">{t("amenities.icon")}</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                                type="text"
                                placeholder={t("common.search")}
                                className="w-full pl-9 p-2 border rounded-lg text-sm"
                                value={iconSearch}
                                onChange={(e) => setIconSearch(e.target.value)}
                            />
                        </div>

                        <div className="grid grid-cols-6 gap-2 max-h-60 overflow-y-auto p-1 border rounded-lg">
                            {filteredIcons.slice(0, 100).map((iconName) => {
                                const Icon = (Icons as any)[iconName];
                                if (!Icon) return null;
                                const isSelected = currentIcon === iconName;
                                return (
                                    <button
                                        key={iconName}
                                        type="button"
                                        onClick={() => setValue("icon", iconName, { shouldDirty: true })}
                                        className={clsx(
                                            "w-10 h-10 rounded-lg flex items-center justify-center transition-all",
                                            isSelected
                                                ? "bg-indigo-600 text-white shadow-md ring-2 ring-indigo-200"
                                                : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-indigo-300"
                                        )}
                                        title={iconName}
                                    >
                                        <Icon size={20} />
                                    </button>
                                );
                            })}
                            {filteredIcons.length > 100 && (
                                <div className="col-span-6 text-center py-2 text-xs text-slate-500">
                                    {filteredIcons.length - 100} more — use search
                                </div>
                            )}
                        </div>
                        {currentIcon && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 p-2 rounded-lg">
                                <span className="font-medium">{t("amenities.selected")}:</span>
                                <span className="flex items-center gap-1.5">
                                    {(() => { const I = (Icons as any)[currentIcon]; return I ? <I size={14} /> : null; })()}
                                    {currentIcon}
                                </span>
                            </div>
                        )}
                        <input type="hidden" {...register("icon")} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={t("common.nameEn")} error={errors.nameEn?.message as string} required>
                            <input {...register("nameEn", { required: "English name is required" })} className="w-full px-3 py-2 border rounded-md" />
                        </FormField>
                        <FormField label={t("common.nameAr")} error={errors.nameAr?.message as string} required>
                            <input {...register("nameAr", { required: "Arabic name is required" })} className="w-full px-3 py-2 border rounded-md" dir="rtl" />
                        </FormField>
                    </div>
                </form>
            </SlideOver>
        </div>
    );
}
